import axios, { AxiosInstance } from 'axios';
import { API_BASE_URL } from '../utils/api';

const TOKEN_KEY = 'engineer_token';
const USER_KEY = 'engineer_user';

export type EngineerUser = {
  id: string;
  email: string;
  name: string;
  role: string;
  companyId: string;
  companyName?: string;
  companyLogoUrl?: string | null;
};

export type EngineerMe = {
  id: string;
  email: string;
  name: string;
  phone?: string | null;
  role: string;
  address?: string | null;
  profilePhotoUrl?: string | null;
  gasSafeNumber?: string | null;
  gasSafeExpiry?: string | null;
  qualifications: string[];
  skills: string[];
  baseAddress?: string | null;
  basePostcode?: string | null;
  serviceRadiusMiles?: number | null;
  maxJobsPerDay?: number | null;
  company: {
    id: string;
    name: string;
    logoUrl?: string | null;
    phone?: string | null;
    email?: string | null;
    address?: string | null;
    gasRegistrationNumber?: string | null;
    vatRate?: number | null;
  } | null;
};

export function getEngineerToken(): string {
  return localStorage.getItem(TOKEN_KEY) || '';
}

export function getEngineerUser(): EngineerUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setEngineerSession(token: string, user: EngineerUser) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearEngineerSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

const engineerApi: AxiosInstance = axios.create({ baseURL: API_BASE_URL });

engineerApi.interceptors.request.use((config) => {
  const token = getEngineerToken();
  if (token) {
    config.headers = config.headers || {};
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

engineerApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      clearEngineerSession();
      if (typeof window !== 'undefined' && !window.location.pathname.endsWith('/engineer/login')) {
        window.location.href = '/engineer/login';
      }
    }
    return Promise.reject(err);
  },
);

// ------------- AUTH -------------

export const engineerAuthApi = {
  async resolveInvite(token: string) {
    const { data } = await engineerApi.get(`/engineer-auth/invite/${token}`);
    return data?.data || data;
  },
  async completeSetup(token: string, payload: { pin: string; name?: string; phone?: string }) {
    const { data } = await engineerApi.post(`/engineer-auth/invite/${token}/complete`, payload);
    if (data?.access_token && data?.user) {
      setEngineerSession(data.access_token, data.user);
    }
    return data;
  },
  async loginWithPin(email: string, pin: string) {
    const { data } = await engineerApi.post(`/engineer-auth/login`, { email, pin });
    if (data?.access_token && data?.user) {
      setEngineerSession(data.access_token, data.user);
    }
    return data;
  },
};

// ------------- ENGINEER API -------------

export type Visit = {
  id: string;
  jobId: string;
  engineerId: string;
  status: 'SCHEDULED' | 'EN_ROUTE' | 'ON_SITE' | 'PAUSED' | 'ENDED' | 'CANCELLED';
  outcome?: string | null;
  enRouteAt?: string | null;
  arrivedAt?: string | null;
  pausedAt?: string | null;
  resumedAt?: string | null;
  endedAt?: string | null;
  pauseReason?: string | null;
  pauseNote?: string | null;
  outcomeNote?: string | null;
  photoUrls?: string[];
  signatureUrl?: string | null;
};

export type EngineerJob = {
  id: string;
  title: string;
  description?: string | null;
  jobType?: string | null;
  status: string;
  priority: string;
  scheduledDate?: string | null;
  completedDate?: string | null;
  address?: string | null;
  postcode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  notes?: string | null;
  duration?: number | null;
  certificateTemplateId?: string | null;
  certificateKind?: string | null;
  client?: {
    id: string;
    name: string;
    phone?: string | null;
    address?: string | null;
    postcode?: string | null;
  } | null;
  site?: {
    id: string;
    name?: string | null;
    address?: string | null;
    postcode?: string | null;
  } | null;
  certificateTemplate?: {
    id: string;
    templateLabel: string;
  } | null;
  visits?: Visit[];
  quote?: any;
  assignedEngineer?: { id: string; name?: string | null; phone?: string | null } | null;
};

export type EngineerCertTemplateMeta = {
  id: string;
  templateLabel: string;
  certificateTypeName: string;
  certificatePrefix: string;
  source: string;
  curatedKey?: string | null;
  schemaJson?: unknown;
  schemaVersion?: number;
};

export const engineerService = {
  async me() {
    const { data } = await engineerApi.get('/engineer/me');
    const me = data as EngineerMe;
    // Keep the locally cached session in sync with latest company info (logo, name).
    try {
      const cached = getEngineerUser();
      if (cached) {
        const nextCached: EngineerUser = {
          ...cached,
          name: me.name || cached.name,
          email: me.email || cached.email,
          companyId: me.company?.id || cached.companyId,
          companyName: me.company?.name || cached.companyName,
          companyLogoUrl: me.company?.logoUrl ?? cached.companyLogoUrl ?? null,
        };
        localStorage.setItem(USER_KEY, JSON.stringify(nextCached));
      }
    } catch {
      /* ignore cache update errors */
    }
    return me;
  },

  async updateMe(patch: Partial<{
    name: string;
    phone: string;
    address: string;
    gasSafeNumber: string | null;
    gasSafeExpiry: string | null;
    qualifications: string[];
    skills: string[];
    baseAddress: string;
    basePostcode: string;
    serviceRadiusMiles: number | null;
    maxJobsPerDay: number | null;
    profilePhotoUrl: string | null;
  }>) {
    const { data } = await engineerApi.patch('/engineer/me', patch);
    return data as EngineerMe;
  },

  async changePin(payload: { currentPin?: string; newPin: string }) {
    const { data } = await engineerApi.post('/engineer-auth/change-pin', payload);
    return data as { success: boolean };
  },

  async getToday(day?: string) {
    const { data } = await engineerApi.get('/engineer/today', {
      params: day ? { day } : {},
    });
    return data as { activeVisits: Visit[]; jobs: EngineerJob[]; day: string };
  },

  async getUpcoming(limit = 20) {
    const { data } = await engineerApi.get('/engineer/upcoming', { params: { limit } });
    return data as EngineerJob[];
  },

  async listJobs(opts: { status?: string; limit?: number } = {}) {
    const { data } = await engineerApi.get('/engineer/jobs', { params: opts });
    return data as EngineerJob[];
  },

  async getMyCertificates() {
    const { data } = await engineerApi.get('/engineer/certificates');
    return data as {
      pending: Array<{
        jobId: string;
        jobTitle: string;
        jobType?: string | null;
        jobStatus: string;
        scheduledDate?: string | null;
        client?: { id: string; name: string } | null;
        requiredSkills?: string[];
        address?: string | null;
        postcode?: string | null;
      }>;
      submitted: Array<{
        id: string;
        certificateNumber: string;
        type: string;
        status: string;
        issueDate?: string | null;
        expiryDate?: string | null;
        fileUrl?: string | null;
        job?: { id: string; title: string } | null;
        client?: { id: string; name: string } | null;
      }>;
    };
  },

  async getJob(id: string) {
    const { data } = await engineerApi.get(`/engineer/jobs/${id}`);
    return data as EngineerJob;
  },

  async startVisit(jobId: string, location?: { lat?: number; lng?: number }) {
    const { data } = await engineerApi.post(`/engineer/jobs/${jobId}/visits`, { location });
    return data as Visit;
  },

  async transition(
    visitId: string,
    action: 'EN_ROUTE' | 'ARRIVED' | 'PAUSE' | 'RESUME' | 'COMPLETE' | 'CANCEL',
    extras: {
      location?: { lat?: number; lng?: number };
      pauseReason?: string;
      pauseNote?: string;
      outcome?: string;
      outcomeNote?: string;
    } = {},
  ) {
    const { data } = await engineerApi.post(`/engineer/visits/${visitId}/transition`, {
      action,
      ...extras,
    });
    return data as Visit;
  },

  async addPhoto(visitId: string, photoUrl: string) {
    const { data } = await engineerApi.post(`/engineer/visits/${visitId}/photos`, { photoUrl });
    return data as Visit;
  },

  async setSignature(visitId: string, signatureUrl: string) {
    const { data } = await engineerApi.post(`/engineer/visits/${visitId}/signature`, { signatureUrl });
    return data as Visit;
  },

  // ------------- CERTIFICATES -------------

  async listCertificateTemplates(jobId: string) {
    const { data } = await engineerApi.get(`/engineer/jobs/${jobId}/certificate-templates`);
    return data as {
      jobId: string;
      templates: Array<
        | {
            fillMode: 'curated';
            id: string;
            templateLabel: string;
            certificateTypeName: string;
            certificatePrefix: string;
            source: string;
            curatedKey?: string | null;
            schemaVersion?: number;
            schema: CuratedSchema;
          }
        | {
            fillMode: 'pdf_wizard';
            id: string;
            templateLabel: string;
            certificateTypeName: string;
            certificatePrefix: string;
            source: string;
            curatedKey?: string | null;
            schemaVersion?: number;
            pdfWizardSummary?: string;
          }
      >;
    };
  },

  async getCertificateTemplate(templateId: string, jobId?: string) {
    const { data } = await engineerApi.get(`/engineer/certificate-templates/${encodeURIComponent(templateId)}`, {
      params: jobId ? { jobId } : undefined,
    });
    return data as
      | {
          fillMode: 'curated';
          templateMeta: EngineerCertTemplateMeta;
          schema: CuratedSchema;
        }
      | {
          fillMode: 'pdf_wizard';
          templateMeta: EngineerCertTemplateMeta;
          pdfWizard: { screens: any[]; fields: any[] };
        };
  },

  async uploadCertAsset(kind: 'signature' | 'photo', dataUrl: string, mime?: string) {
    const { data } = await engineerApi.post('/engineer/certificate-assets', {
      kind,
      data: dataUrl,
      mime,
    });
    return data as { path: string; signedUrl: string };
  },

  // ------------- QUOTES (on-site quick quotes) -------------

  async getPriceBook() {
    const { data } = await engineerApi.get('/engineer/price-book');
    return data as {
      categories: Array<{ id: string; name: string; sortOrder: number }>;
      items: Array<{
        id: string;
        name: string;
        description?: string | null;
        sku?: string | null;
        categoryId?: string | null;
        pricingModel: 'FIXED' | 'HOURLY' | 'RANGE';
        unit: string;
        unitPrice: number;
        minPrice?: number | null;
        maxPrice?: number | null;
        vatApplies: boolean;
        active: boolean;
      }>;
      policy: {
        id: string;
        model: 'THRESHOLD' | 'ALWAYS_ADMIN' | 'TRUST';
        thresholdAmount?: number | null;
        maxDiscountPct: number;
        validDays: number;
        allowCustomItems: boolean;
        requireCustomerSignature: boolean;
        autoConvertOnAcceptance: boolean;
        notifyAdminsOnSubmit: boolean;
      };
    };
  },

  async listMyQuotes() {
    const { data } = await engineerApi.get('/engineer/quotes');
    return data as Array<{
      id: string;
      quoteNumber: string;
      jobTitle: string;
      status: string;
      adminApprovalStatus?: string | null;
      totalAmount: number;
      createdAt: string;
      validUntil?: string | null;
      items?: Array<{
        id: string;
        description: string;
        quantity: number;
        unitPrice: number;
        totalPrice: number;
      }>;
    }>;
  },

  async getMyQuote(id: string) {
    const { data } = await engineerApi.get(`/engineer/quotes/${id}`);
    return data;
  },

  async createQuote(payload: {
    clientId: string;
    jobId?: string | null;
    jobTitle: string;
    description?: string;
    notes?: string;
    siteAddress?: string;
    sitePostcode?: string;
    customerName?: string;
    signatureUrl?: string;
    items: Array<{
      priceBookItemId?: string | null;
      description: string;
      quantity: number;
      unitPrice: number;
      custom?: boolean;
    }>;
  }) {
    const { data } = await engineerApi.post('/engineer/quotes', payload);
    return data;
  },

  async submitCertificate(payload: {
    jobId: string;
    templateId: string;
    schemaVersion?: number;
    filledData: Record<string, any>;
    notes?: string;
    expiryDate?: string;
  }) {
    const { data } = await engineerApi.post('/engineer/certificates', payload);
    return data as { id: string; certificateNumber: string; signedUrl: string | null; jobId: string };
  },
};

// ------------- CURATED SCHEMA TYPES (mirror of backend DSL) -------------

export type CuratedFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'select'
  | 'multiselect'
  | 'date'
  | 'time'
  | 'datetime'
  | 'signature'
  | 'photo'
  | 'passfail'
  | 'address';

export type CuratedField = {
  key: string;
  label: string;
  type: CuratedFieldType;
  required?: boolean;
  hint?: string;
  placeholder?: string;
  options?: { value: string; label: string }[];
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    maxLength?: number;
    unit?: string;
    advisoryMin?: number;
    advisoryMax?: number;
  };
  visibleWhen?: { key: string; in: Array<string | boolean> };
  default?: string | number | boolean | null;
};

export type CuratedSection = {
  key: string;
  label: string;
  description?: string;
  fields: CuratedField[];
};

export type CuratedGroup = {
  key: string;
  label: string;
  description?: string;
  repeatable?: { min: number; max: number; labelTemplate: string };
  sections: CuratedSection[];
};

export type CuratedSchema = {
  key: string;
  title: string;
  authority?: string;
  defaultPrefix: string;
  summary: string;
  version: number;
  groups: CuratedGroup[];
};

export async function getBrowserLocation(timeoutMs = 5000): Promise<{ lat: number; lng: number } | undefined> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return undefined;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(undefined),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30_000 },
    );
  });
}
